import { useEffect } from 'react'
import { clsx } from 'clsx'
import capitalize from 'lodash/capitalize'

import {
  UpgradeDialog,
  Dialog,
  Button,
  List,
  useBreakpoint,
  PlusIcon,
  Paragraph,
  CheckIcon,
} from '@buffer-mono/popcorn'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'
import { OBPlanIdValues, UpdateType } from '../../../PlanSelector/types'
import type { ObPlanId } from '../../../../gql/graphql'
import { MODALS } from '../../../../common/types'

import { useCrossAppNavigate } from '../../../../common/hooks/useCrossAppNavigate'
import { getPublishUrl } from '@buffer-mono/shared-utils/urls'
import { ORCHESTRATOR_ACTIONS } from '../../../../common/events/orchestratorEvents'

import styles from './SubscriptionUpdateModal.module.css'
import trackTrialWelcomeModalViewed from '../../../../tracking/TrialPaths/trackTrialWelcomeModalViewed'
import { useUser } from '../../../../common/context/User'

export interface SubscriptionUpdate {
  updateType: UpdateType
  planId: ObPlanId
  currentPlanId?: ObPlanId
  channelCount?: number
  scheduledUpdate?: {
    effectiveDate: string
    newPlan: ObPlanId
    newInterval: string
    newQuantity: number
  } | null
}

export interface SubscriptionUpdateModalProps {
  // Optional props for direct rendering
  open?: boolean
  onOpenChange?: (open: boolean) => void
  subscriptionUpdate?: SubscriptionUpdate
  startedTrial?: boolean
  isDowngradingToFreePlan?: boolean
  // Legacy props (kept for compatibility but not used when direct rendering)
  planId?: ObPlanId
  planName?: string
}

interface PlanContentProps {
  planId: ObPlanId
  type: UpdateType
}

// Format the effective date (normalize date-only ISO to UTC midnight to avoid TZ shifts)
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'your next billing date'
  const normalizeIso = (v: string) => (v.length === 10 ? `${v}T00:00:00Z` : v)
  const date = new Date(normalizeIso(dateString))
  if (Number.isNaN(date.getTime())) return 'your next billing date'
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

const EssentialsPlanContent: React.FC<{
  dismissModal: () => void
  subscriptionUpdate?: SubscriptionUpdate
}> = ({ dismissModal, subscriptionUpdate: propSubscriptionUpdate }) => {
  const { modalData } = useModalManager()
  const subscriptionUpdate =
    propSubscriptionUpdate || modalData?.subscriptionUpdate
  const channelCount = subscriptionUpdate?.channelCount

  return (
    <>
      <UpgradeDialog.TextContent>
        <UpgradeDialog.Title>Welcome to Essentials! 🎉</UpgradeDialog.Title>
        <Dialog.Description>
          You&apos;ve successfully upgraded to the{' '}
          <strong>
            Essentials Plan
            {channelCount
              ? ` with ${channelCount} ${
                  channelCount === 1 ? 'channel' : 'channels'
                }`
              : ''}
            .
          </strong>{' '}
          The plan change is already in place!
        </Dialog.Description>
        <List className={styles.featureList}>
          <List.Item icon={CheckIcon}>
            Unlimited post scheduling, threads, ideas and tags.
          </List.Item>
          <List.Item icon={CheckIcon}>
            Advanced analytics and engagement tools.
          </List.Item>
          <List.Item icon={CheckIcon}>Hashtag manager and more!</List.Item>
        </List>
        <UpgradeDialog.Actions>
          <Button variant="primary" size="large" onClick={() => dismissModal()}>
            Got It
          </Button>
        </UpgradeDialog.Actions>
      </UpgradeDialog.TextContent>
      <UpgradeDialog.Image>
        <img
          src="https://buffer-ui.s3.amazonaws.com/illustrations/essentials-plan.png"
          alt="Essentials Plan Illustration"
        />
      </UpgradeDialog.Image>
    </>
  )
}

const TeamPlanContent: React.FC<{ dismissModal: () => void }> = ({
  dismissModal,
}) => {
  const crossAppNavigate = useCrossAppNavigate()

  const handleInviteTeam = (): void => {
    dismissModal()
    // Navigate to publish settings team page
    crossAppNavigate(getPublishUrl('settings/team'))
  }

  return (
    <>
      <UpgradeDialog.TextContent>
        <UpgradeDialog.Title>
          Welcome to the Team plan! 🎉 Collaborate like never before
        </UpgradeDialog.Title>
        <Dialog.Description>
          Invite your teammates to manage and schedule content together:
        </Dialog.Description>
        <List className={styles.featureList}>
          <List.Item icon={CheckIcon}>
            Assign roles and permissions and keep things organized.
          </List.Item>
          <List.Item icon={CheckIcon}>
            Use approvals to streamline your workflow.
          </List.Item>
          <List.Item icon={CheckIcon}>
            Collaborate across drafts and posts.
          </List.Item>
        </List>
        <UpgradeDialog.Actions>
          <Button variant="primary" size="large" onClick={handleInviteTeam}>
            Invite Team
          </Button>
        </UpgradeDialog.Actions>
      </UpgradeDialog.TextContent>
      <UpgradeDialog.Image>
        <img
          src="https://buffer-ui.s3.amazonaws.com/illustrations/team-plan.png"
          alt="Team Plan Illustration"
        />
      </UpgradeDialog.Image>
    </>
  )
}

const ChannelIncreaseContent: React.FC<{ dismissModal: () => void }> = ({
  dismissModal,
}) => {
  const handleConnectChannels = (): void => {
    dismissModal()
    // Open the Channel Storefront dialog
    ORCHESTRATOR_ACTIONS.openChannelStorefront({
      cta: 'channel-increase-modal',
    })
  }

  return (
    <>
      <UpgradeDialog.TextContent>
        <UpgradeDialog.Title>
          You&apos;ve Got More Channel Slots Available!
        </UpgradeDialog.Title>
        <Paragraph>
          Your plan just got an upgrade, you can now connect more social
          channels to save time, stay organized, and manage everything in one
          place.
        </Paragraph>
        <UpgradeDialog.Actions>
          <Button
            variant="primary"
            size="large"
            onClick={handleConnectChannels}
          >
            <PlusIcon /> Connect Channels
          </Button>
        </UpgradeDialog.Actions>
      </UpgradeDialog.TextContent>
      <UpgradeDialog.Image className={styles.channelIncreaseImageContainer}>
        <img
          src="https://buffer-ui.s3.amazonaws.com/illustrations/add-channels.png"
          alt="Add Channels Illustration"
        />
      </UpgradeDialog.Image>
    </>
  )
}

const ChannelDecreaseContent: React.FC<{
  dismissModal: () => void
  subscriptionUpdate?: SubscriptionUpdate
}> = ({ dismissModal, subscriptionUpdate: propSubscriptionUpdate }) => {
  const { modalData } = useModalManager()
  const subscriptionUpdate =
    propSubscriptionUpdate || modalData?.subscriptionUpdate
  const { scheduledUpdate } = subscriptionUpdate || {}

  const changesDateCopy = `Changes will take place on ${formatDate(
    scheduledUpdate?.effectiveDate,
  )}`
  const crossAppNavigate = useCrossAppNavigate()

  const handleGoToChannels = (): void => {
    dismissModal()
    // Navigate to channels page
    crossAppNavigate(getPublishUrl('settings/channels'))
  }

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>
          Done! Your Plan Now Includes Fewer
          <br /> Channel Slots ✅
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Description>
          <Paragraph>
            Some connected channels may be locked. You can increase your
            channels at any time from your channel settings.{' '}
            <a
              href="https://support.buffer.com/articles/unlocking-a-locked-channel-in-buffer-TWBz1u0FmT?utm_source=subscription-downgrade-dialog&utm_medium=product-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about locked channels
            </a>
          </Paragraph>
          {scheduledUpdate?.effectiveDate && (
            <>
              <br />
              <Paragraph>{changesDateCopy}</Paragraph>{' '}
            </>
          )}
        </Dialog.Description>
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant="primary" size="large" onClick={handleGoToChannels}>
          Go to Channels
        </Button>
      </Dialog.Footer>
    </>
  )
}

const SubscriptionDowngradeContent: React.FC<{
  dismissModal: () => void
  subscriptionUpdate?: SubscriptionUpdate
  isDowngradingToFreePlan?: boolean
}> = ({
  dismissModal,
  subscriptionUpdate: propSubscriptionUpdate,
  isDowngradingToFreePlan: propIsDowngradingToFreePlan,
}) => {
  const { modalData } = useModalManager()
  const subscriptionUpdate =
    propSubscriptionUpdate || modalData?.subscriptionUpdate
  const isDowngradingToFreePlan =
    propIsDowngradingToFreePlan ?? modalData?.isDowngradingToFreePlan
  const { scheduledUpdate } = subscriptionUpdate || {}

  const endDate = formatDate(scheduledUpdate?.effectiveDate)

  const formatPlanName = (plan?: ObPlanId | string | null): string => {
    if (!plan) return 'current'

    // Convert to lowercase string for comparison to handle both string and enum types
    const planString = plan.toLowerCase()

    // Use capitalize from lodash to ensure proper capitalization
    switch (planString) {
      case 'free':
        return 'Free'
      case 'essentials':
        return 'Essentials'
      case 'team':
        return 'Team'
      case 'agency':
        return 'Agency'
      default:
        // If we get an unexpected plan name, capitalize it
        return capitalize(planString)
    }
  }
  const currentPlanName = formatPlanName(
    subscriptionUpdate?.currentPlanId || subscriptionUpdate?.planId,
  )

  const newPlanName = isDowngradingToFreePlan
    ? formatPlanName(OBPlanIdValues.free)
    : formatPlanName(scheduledUpdate?.newPlan as ObPlanId)

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>Your Plan Was Downgraded ✅</Dialog.Title>
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Description>
          You&apos;ve scheduled a downgrade from the{' '}
          <strong>{currentPlanName}</strong> plan to the{' '}
          <strong>{newPlanName}</strong> plan. You will continue to have access
          to <strong>{currentPlanName}</strong> features until{' '}
          <strong>{endDate}</strong>.
          <br />
          <br />
          You can always upgrade your plan from the Plans and Pricing page in
          your settings.
        </Dialog.Description>
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant="primary" size="large" onClick={() => dismissModal()}>
          Got It
        </Button>
      </Dialog.Footer>
    </>
  )
}

interface ContentProps extends PlanContentProps {
  dismissModal: () => void
  subscriptionUpdate?: SubscriptionUpdate
  isDowngradingToFreePlan?: boolean
}

const Content: React.FC<ContentProps> = ({
  planId,
  type,
  dismissModal,
  subscriptionUpdate,
  isDowngradingToFreePlan,
}) => {
  // Handle update type specific content first
  if (type === UpdateType.ChannelIncrease) {
    return <ChannelIncreaseContent dismissModal={dismissModal} />
  }

  if (type === UpdateType.SubscriptionDowngrade) {
    return (
      <SubscriptionDowngradeContent
        dismissModal={dismissModal}
        subscriptionUpdate={subscriptionUpdate}
        isDowngradingToFreePlan={isDowngradingToFreePlan}
      />
    )
  }

  if (type === UpdateType.ChannelDecrease) {
    return (
      <ChannelDecreaseContent
        dismissModal={dismissModal}
        subscriptionUpdate={subscriptionUpdate}
      />
    )
  }

  if (
    type === UpdateType.SubscriptionUpgrade &&
    planId === OBPlanIdValues.essentials
  ) {
    return (
      <EssentialsPlanContent
        dismissModal={dismissModal}
        subscriptionUpdate={subscriptionUpdate}
      />
    )
  }

  if (
    type === UpdateType.SubscriptionUpgrade &&
    planId === OBPlanIdValues.team
  ) {
    return <TeamPlanContent dismissModal={dismissModal} />
  }

  return <EssentialsPlanContent dismissModal={dismissModal} />
}

const SubscriptionUpdateModal: React.FC<SubscriptionUpdateModalProps> = (
  props,
) => {
  const modalManager = useModalManager()
  const breakpoint = useBreakpoint()
  const user = useUser()

  // Use props if provided, otherwise fall back to modal manager
  const subscriptionUpdate =
    props.subscriptionUpdate || modalManager.modalData?.subscriptionUpdate
  const startedTrial =
    props.startedTrial ?? modalManager.modalData?.startedTrial
  const isDowngradingToFreePlan =
    props.isDowngradingToFreePlan ??
    modalManager.modalData?.isDowngradingToFreePlan

  // Determine if modal is open based on mode
  const isOpenFromProps = props.open ?? false
  const isOpenFromModalManager =
    modalManager.modalKey === MODALS.subscriptionUpdateModern
  const isOpen =
    props.open !== undefined ? isOpenFromProps : isOpenFromModalManager

  const { updateType, planId } = subscriptionUpdate || {}

  const isMobile = breakpoint === 'sm' || breakpoint === 'initial'

  const handleClose = (): void => {
    if (props.onOpenChange) {
      // Direct mode - use callback
      props.onOpenChange(false)
    } else {
      // App-shell mode - use modal manager
      modalManager.dismissModal()
    }
  }

  const dismissModal = handleClose

  /*
   * A user could be upgrading, downgrading, or just updating their plan channel count or billing interval
   * This is used to determine the content of the dialog
   * This isnt fully implemented yet but will be coming soon
   */
  const dialogType = updateType || UpdateType.SubscriptionUpdate
  const hasPerformedDowngradingAction =
    dialogType === UpdateType.SubscriptionDowngrade ||
    dialogType === UpdateType.ChannelDecrease
  const dialogSize = hasPerformedDowngradingAction ? 'medium' : 'xlarge'

  useEffect(() => {
    const upgradePathName = 'trialWelcomeModal'
    const payload = {
      ctaButton: 'trialStarted',
      upgradePathName,
      ctaView: 'trialWelcomeModal',
      ctaLocation: 'trialWelcomeModal',
    }
    if (startedTrial) {
      trackTrialWelcomeModalViewed({
        payload,
        user,
      })
    }
  }, [user, startedTrial])

  if (!subscriptionUpdate || !isOpen) {
    return null
  }

  // Use regular Dialog for downgrade modals (they have specific header/body/footer structure)
  if (hasPerformedDowngradingAction) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <Dialog.Content
          className={clsx(
            isMobile && styles.mobileDialog,
            styles.downgradingDialog,
          )}
          size={dialogSize}
        >
          {dialogType !== UpdateType.ChannelDecrease && <Dialog.CloseButton />}
          <Content
            type={dialogType}
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            planId={planId as ObPlanId}
            dismissModal={dismissModal}
            subscriptionUpdate={subscriptionUpdate}
            isDowngradingToFreePlan={isDowngradingToFreePlan}
          />
        </Dialog.Content>
      </Dialog>
    )
  }

  // Use UpgradeDialog for upgrade modals
  return (
    <UpgradeDialog open={isOpen} onOpenChange={handleClose}>
      <UpgradeDialog.Content
        className={clsx(isMobile && styles.mobileDialog, styles.dialog)}
        size={dialogSize}
      >
        <Content
          type={dialogType}
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          planId={planId as ObPlanId}
          dismissModal={dismissModal}
          subscriptionUpdate={subscriptionUpdate}
          isDowngradingToFreePlan={isDowngradingToFreePlan}
        />
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}

export default SubscriptionUpdateModal
